/**
 * Subpage_102.jsx
 * ------------------------------------------------------------
 * Découpage Ingénierie Système :
 *   - Tab A : Activity (WBS)
 *   - Tab B : Function (FBS)
 *   - Tab C : Product (PBS)
 *   - Tab D : Location (LBS)
 *   - Tab E : Discipline (DBS)
 *   - Tab F : Cross-View (matrice croisée)
 * ------------------------------------------------------------
 */

import React, { useState, useRef } from "react";
import "../../styles/main.css";
import theme from "../../styles/theme";

import TabsBase from "../../components/reusable/Navigation/TabsBase";
import ContainerBase from "../../components/framepart/SubpageLayout/ContainerBase";
import ToolbarHeadSubpage from "../../components/framepart/SubpageLayout/ToolbarHeadSubpage";

import DropdownMulti from "../../components/reusable/Forms/DropdownMulti";
import Button from "../../components/reusable/Forms/Button";
import TextFilter from "../../components/reusable/Forms/TextFilter";
import Switch from "../../components/reusable/Forms/Switch";

import CoreliaTreeView from "../../components/defined/Corelia/CoreliaTreeView";
import TreeViewSideToolbar from "../../components/defined/Corelia/TreeViewSideToolbar";
import CrossView from "../../components/defined/Corelia/CrossView";

/* ============================================================
   OPTIONS GÉNÉRIQUES POUR DROPDOWN (toolbar)
============================================================ */
const demoOptions = [
  { value: "opt1", label: "Option 1" },
  { value: "opt2", label: "Option 2" },
  { value: "opt3", label: "Option 3" },
];

/* ============================================================
   PAIRS D’ANALYSE CROSS-VIEW (Axe X / Axe Y)
============================================================ */
const CROSS_VIEW_PAIRS = [
  {
    id: "A-F",
    label: "Activity ↔ Function",
    axisX: { id: "A", label: "Activities (A)" },
    axisY: { id: "F", label: "Functions (F)" },
  },
  {
    id: "A-P",
    label: "Activity ↔ Product",
    axisX: { id: "A", label: "Activities (A)" },
    axisY: { id: "P", label: "Products (P)" },
  },
  {
    id: "F-L",
    label: "Function ↔ Location",
    axisX: { id: "F", label: "Functions (F)" },
    axisY: { id: "L", label: "Locations (L)" },
  },
  {
    id: "F-D",
    label: "Function ↔ Discipline",
    axisX: { id: "F", label: "Functions (F)" },
    axisY: { id: "D", label: "Disciplines" },
  },
];

/* ============================================================
   FACTORISATION — Styles communs aux containers
============================================================ */
const baseLeftZoneStyle = {
  flex: 3,
  minWidth: 700,
  minHeight: "calc(100vh - 290px)",
  padding: 12,
  borderRadius: 6,
  background: theme.colors.background.surface,
  border: `1px solid ${theme.colors.borders.default}`,
  overflowX: "auto",
  overflowY: "auto",
  flexShrink: 0,
};

const baseRightZoneStyle = {
  flex: 1,
  minWidth: 350,
  minHeight: "calc(100vh - 290px)",
  padding: 12,
  borderRadius: 6,
  background: theme.colors.background.surface,
  border: `1px solid ${theme.colors.borders.default}`,
  overflowX: "auto",
  overflowY: "auto",
  boxSizing: "border-box",
  flexShrink: 0,
};

/* ============================================================
   HELPER CODES INCRÉMENTAUX POUR LES RACINES
============================================================ */
/**
 * Calcule le prochain code racine sous la forme `${prefix}-N`
 * ex. prefix="F", racines existantes : F-1, F-2 → retourne "F-3"
 */
const getNextRootCode = (prefix, roots) => {
  const regex = new RegExp(`^${prefix}-(\\d+)$`);
  let max = 0;

  (roots || []).forEach((r) => {
    if (typeof r.code !== "string") return;
    const match = r.code.match(regex);
    if (!match) return;
    const num = parseInt(match[1], 10);
    if (!Number.isNaN(num) && num > max) max = num;
  });

  return `${prefix}-${max + 1}`;
};

/* ============================================================
   PAGE
============================================================ */
const Subpage_102 = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [switchOn, setSwitchOn] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedCategories, setSelectedCategories] = useState([]);

  /* ------------------------------------------------------------
     Refs pour les 5 arbres (toolbar verticale)
  ------------------------------------------------------------ */
  const activityTreeRef = useRef(null);
  const functionTreeRef = useRef(null);
  const productTreeRef = useRef(null);
  const locationTreeRef = useRef(null);
  const disciplineTreeRef = useRef(null);

  /* ------------------------------------------------------------
     Arbre Activity (WBS) — format CoreliaTreeView
  ------------------------------------------------------------ */
  const initialActivityTree = [
    {
      id: "A-1",
      code: "A-1",
      label: "Projet Eau Potable",
      typeCode: "A",
      description: "",
      children: [
        {
          id: "A-1.1",
          code: "A-1.1",
          label: "Études préliminaires",
          typeCode: "A",
          description: "",
          children: [
            {
              id: "A-1.1.1",
              code: "A-1.1.1",
              label: "Analyse de site",
              typeCode: "A",
              description: "",
              children: [],
            },
            {
              id: "A-1.1.2",
              code: "A-1.1.2",
              label: "Données topographiques",
              typeCode: "A",
              description: "",
              children: [],
            },
          ],
        },
        {
          id: "A-1.2",
          code: "A-1.2",
          label: "Travaux d'exécution",
          typeCode: "A",
          description: "",
          children: [],
        },
      ],
    },
  ];
  const [activityTree, setActivityTree] = useState(initialActivityTree);

  /* ------------------------------------------------------------
     Arbre Function (FBS)
  ------------------------------------------------------------ */
  const initialFunctionTree = [
    {
      id: "F-1",
      code: "F-1",
      label: "Fonctions globales du système",
      typeCode: "F",
      description: "",
      children: [
        {
          id: "F-1.1",
          code: "F-1.1",
          label: "Assurer la fourniture d'eau potable",
          typeCode: "F",
          description: "",
          children: [
            {
              id: "F-1.1.1",
              code: "F-1.1.1",
              label: "Captage et adduction",
              typeCode: "F",
              description: "",
              children: [],
            },
            {
              id: "F-1.1.2",
              code: "F-1.1.2",
              label: "Traitement et potabilisation",
              typeCode: "F",
              description: "",
              children: [],
            },
            {
              id: "F-1.1.3",
              code: "F-1.1.3",
              label: "Distribution aux usagers",
              typeCode: "F",
              description: "",
              children: [],
            },
          ],
        },
        {
          id: "F-1.2",
          code: "F-1.2",
          label: "Assurer la continuité du service",
          typeCode: "F",
          description: "",
          children: [
            {
              id: "F-1.2.1",
              code: "F-1.2.1",
              label: "Sécuriser l'alimentation électrique",
              typeCode: "F",
              description: "",
              children: [],
            },
            {
              id: "F-1.2.2",
              code: "F-1.2.2",
              label: "Redondance des équipements critiques",
              typeCode: "F",
              description: "",
              children: [],
            },
          ],
        },
      ],
    },
  ];
  const [functionTree, setFunctionTree] = useState(initialFunctionTree);

  /* ------------------------------------------------------------
     Arbre Product (PBS)
  ------------------------------------------------------------ */
  const initialProductTree = [
    {
      id: "P-1",
      code: "P-1",
      label: "Système global",
      typeCode: "P",
      description: "",
      children: [
        {
          id: "P-1.1",
          code: "P-1.1",
          label: "Unité de captage",
          typeCode: "P",
          description: "",
          children: [],
        },
        {
          id: "P-1.2",
          code: "P-1.2",
          label: "Station de traitement",
          typeCode: "P",
          description: "",
          children: [],
        },
        {
          id: "P-1.3",
          code: "P-1.3",
          label: "Réseau de distribution",
          typeCode: "P",
          description: "",
          children: [],
        },
      ],
    },
  ];
  const [productTree, setProductTree] = useState(initialProductTree);

  /* ------------------------------------------------------------
     Arbre Location (LBS)
  ------------------------------------------------------------ */
  const initialLocationTree = [
    {
      id: "L-1",
      code: "L-1",
      label: "Périmètre projet",
      typeCode: "L",
      description: "",
      children: [
        {
          id: "L-1.1",
          code: "L-1.1",
          label: "Site de captage",
          typeCode: "L",
          description: "",
          children: [],
        },
        {
          id: "L-1.2",
          code: "L-1.2",
          label: "Site de traitement",
          typeCode: "L",
          description: "",
          children: [],
        },
        {
          id: "L-1.3",
          code: "L-1.3",
          label: "Zone urbaine de distribution",
          typeCode: "L",
          description: "",
          children: [],
        },
      ],
    },
  ];
  const [locationTree, setLocationTree] = useState(initialLocationTree);

  /* ------------------------------------------------------------
     Arbre Discipline (DBS)
     typeCode = trigramme (CIV / ARC / ELE / …)
  ------------------------------------------------------------ */
  const initialDisciplineTree = [
    {
      id: "D-1",
      code: "D-1",
      label: "Equipes Ingénierie",
      typeCode: "CIV",
      description: "",
      children: [
        {
          id: "D-1.1",
          code: "D-1.1",
          label: "Génie Civil",
          typeCode: "CIV",
          description: "",
          children: [],
        },
        {
          id: "D-1.2",
          code: "D-1.2",
          label: "Architecture",
          typeCode: "ARC",
          description: "",
          children: [],
        },
        {
          id: "D-1.3",
          code: "D-1.3",
          label: "Electricité",
          typeCode: "ELE",
          description: "",
          children: [],
        },
        {
          id: "D-1.4",
          code: "D-1.4",
          label: "Process eau",
          typeCode: "WPR",
          description: "",
          children: [],
        },
        {
          id: "D-1.5",
          code: "D-1.5",
          label: "Hydraulique",
          typeCode: "WPR",
          description: "",
          children: [],
        },
      ],
    },
    {
      id: "D-2",
      code: "D-2",
      label: "Equipes Travaux",
      typeCode: "CIV",
      description: "",
      children: [
        {
          id: "D-2.1",
          code: "D-2.1",
          label: "Directeur de projet",
          typeCode: "PDM",
          description: "",
          children: [],
        },
        {
          id: "D-2.2",
          code: "D-2.2",
          label: "Ingénieur HSE",
          typeCode: "HSE",
          description: "",
          children: [],
        },
      ]
    },  
  ];
  const [disciplineTree, setDisciplineTree] = useState(initialDisciplineTree);

  /* ------------------------------------------------------------
     Relations Cross-View (matrix)
     structure : { "A→F": { "idX|idY": true }, ... }
  ------------------------------------------------------------ */
  const [crossRelations, setCrossRelations] = useState({});

  /* ============================================================
     ZONE A1 — Activity (WBS) — CoreliaTreeView
  ============================================================ */
  const zoneA1 = {
    style: baseLeftZoneStyle,
    gap: 16,
    sectionGap: 6,
    sections: [
      {
        title: "Structure du projet (WBS — Activities)",
        content: (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 10,
              width: "100%",
              height: "100%",
            }}
          >
            <TreeViewSideToolbar
              onAddRoot={() => {
                setActivityTree((prev) => {
                  const nextCode = getNextRootCode("A", prev);
                  const newNode = {
                    id: nextCode,
                    code: nextCode,
                    label: "Nouvelle activité racine",
                    typeCode: "A",
                    description: "",
                    children: [],
                  };
                  return [...prev, newNode];
                });
              }}
              onExpandAll={() => activityTreeRef.current?.expandAll?.()}
              onCollapseAll={() =>
                activityTreeRef.current?.collapseAll?.()
              }
              onResetFilter={() => setSearchTerm("")}
              onExport={() => {
                const blob = new Blob(
                  [JSON.stringify(activityTree, null, 2)],
                  { type: "application/json" }
                );
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "wbs-activities.json";
                a.click();
                URL.revokeObjectURL(url);
              }}
            />

            <div
              style={{
                flex: 1,
                overflow: "auto",
                paddingLeft: 0,
                paddingRight: 250,
              }}
            >
              <CoreliaTreeView
                ref={activityTreeRef}
                data={activityTree}
                onChange={setActivityTree}
                onNodeSelect={(n) => console.log("Select A:", n)}
                onNodeHover={() => {}}
                allowEdit={true}
                allowDragDrop={true}
                allowMultiroot={false}
                filter={searchTerm}
                highlight={null}
              />
            </div>
          </div>
        ),
      },
    ],
  };

  /* ============================================================
     ZONE A2 — Informations Activity
  ============================================================ */
  const zoneA2 = {
    style: baseRightZoneStyle,
    gap: 12,
    sectionGap: 6,
    sections: [
      {
        icon: "/assets/icones/container/shield.svg",
        title: "Informations — WBS (Activities)",
        content: (
          <div
            style={{
              fontSize: theme.fontSizes.sm,
              color: theme.colors.text.body,
            }}
          >
            <p>
              Cette vue représente la décomposition en activités du projet
              (Work Breakdown Structure).
            </p>
            <p>
              Le type <code>A</code> applique la forme <code>square</code> et
              la couleur <code>{theme.systemBreakdown.getColor("A")}</code>.
            </p>
          </div>
        ),
      },
    ],
  };

  /* ============================================================
     ZONE B1 — Function (FBS) — CoreliaTreeView
  ============================================================ */
  const zoneB1 = {
    style: baseLeftZoneStyle,
    gap: 16,
    sectionGap: 6,
    sections: [
      {
        title: "Structure fonctionnelle (FBS — Functions)",
        content: (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 10,
              width: "100%",
              height: "100%",
            }}
          >
            <TreeViewSideToolbar
              onAddRoot={() => {
                setFunctionTree((prev) => {
                  const nextCode = getNextRootCode("F", prev);
                  const newNode = {
                    id: nextCode,
                    code: nextCode,
                    label: "Nouvelle fonction racine",
                    typeCode: "F",
                    description: "",
                    children: [],
                  };
                  return [...prev, newNode];
                });
              }}
              onExpandAll={() => functionTreeRef.current?.expandAll?.()}
              onCollapseAll={() =>
                functionTreeRef.current?.collapseAll?.()
              }
              onResetFilter={() => setSearchTerm("")}
              onExport={() => {
                const blob = new Blob(
                  [JSON.stringify(functionTree, null, 2)],
                  { type: "application/json" }
                );
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "fbs-functions.json";
                a.click();
                URL.revokeObjectURL(url);
              }}
            />

            <div
              style={{
                flex: 1,
                overflow: "auto",
                paddingLeft: 0,
                paddingRight: 250,
              }}
            >
              <CoreliaTreeView
                ref={functionTreeRef}
                data={functionTree}
                onChange={setFunctionTree}
                onNodeSelect={(n) => console.log("Select F:", n)}
                onNodeHover={() => {}}
                allowEdit={true}
                allowDragDrop={true}
                allowMultiroot={false}
                filter={searchTerm}
                highlight={null}
              />
            </div>
          </div>
        ),
      },
    ],
  };

  /* ============================================================
     ZONE B2 — Informations Function
  ============================================================ */
  const zoneB2 = {
    style: baseRightZoneStyle,
    gap: 12,
    sectionGap: 6,
    sections: [
      {
        icon: "/assets/icones/container/shield.svg",
        title: "Informations — FBS (Functions)",
        content: (
          <div
            style={{
              fontSize: theme.fontSizes.sm,
              color: theme.colors.text.body,
            }}
          >
            <p>
              Cette vue représente la décomposition fonctionnelle (Function
              Breakdown Structure).
            </p>
            <p>
              Le type <code>F</code> applique la forme <code>diamond</code> et
              la couleur <code>{theme.systemBreakdown.getColor("F")}</code>.
            </p>
          </div>
        ),
      },
    ],
  };

  /* ============================================================
     ZONE C1 — Product (PBS) — CoreliaTreeView
  ============================================================ */
  const zoneC1 = {
    style: baseLeftZoneStyle,
    gap: 16,
    sectionGap: 6,
    sections: [
      {
        title: "Composez l'installation",
        content: (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 10,
              width: "100%",
              height: "100%",
            }}
          >
            <TreeViewSideToolbar
              onAddRoot={() => {
                setProductTree((prev) => {
                  const nextCode = getNextRootCode("P", prev);
                  const newNode = {
                    id: nextCode,
                    code: nextCode,
                    label: "Nouveau produit racine",
                    typeCode: "P",
                    description: "",
                    children: [],
                  };
                  return [...prev, newNode];
                });
              }}
              onExpandAll={() => productTreeRef.current?.expandAll?.()}
              onCollapseAll={() =>
                productTreeRef.current?.collapseAll?.()
              }
              onResetFilter={() => setSearchTerm("")}
              onExport={() => {
                const blob = new Blob(
                  [JSON.stringify(productTree, null, 2)],
                  { type: "application/json" }
                );
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "pbs-products.json";
                a.click();
                URL.revokeObjectURL(url);
              }}
            />

            <div
              style={{
                flex: 1,
                overflow: "auto",
                paddingLeft: 0,
                paddingRight: 250,
              }}
            >
              <CoreliaTreeView
                ref={productTreeRef}
                data={productTree}
                onChange={setProductTree}
                onNodeSelect={(n) => console.log("Select P:", n)}
                onNodeHover={() => {}}
                allowEdit={true}
                allowDragDrop={true}
                allowMultiroot={false}
                filter={searchTerm}
                highlight={null}
              />
            </div>
          </div>
        ),
      },
    ],
  };

  /* ============================================================
     ZONE C2 — Informations Product
  ============================================================ */
  const zoneC2 = {
    style: baseRightZoneStyle,
    gap: 12,
    sectionGap: 6,
    sections: [
      {
        icon: "/assets/icones/container/shield.svg",
        title: "Informations — PBS (Products)",
        content: (
          <div
            style={{
              fontSize: theme.fontSizes.sm,
              color: theme.colors.text.body,
            }}
          >
            <p>
              Cette vue représente la décomposition en produits (Product
              Breakdown Structure).
            </p>
            <p>
              Le type <code>P</code> applique une forme hexagonale et la
              couleur <code>{theme.systemBreakdown.getColor("P")}</code>.
            </p>
          </div>
        ),
      },
    ],
  };

  /* ============================================================
     ZONE D1 — Location (LBS) — CoreliaTreeView
  ============================================================ */
  const zoneD1 = {
    style: baseLeftZoneStyle,
    gap: 16,
    sectionGap: 6,
    sections: [
      {
        title: "Structure localisation (LBS — Locations)",
        content: (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 10,
              width: "100%",
              height: "100%",
            }}
          >
            <TreeViewSideToolbar
              onAddRoot={() => {
                setLocationTree((prev) => {
                  const nextCode = getNextRootCode("L", prev);
                  const newNode = {
                    id: nextCode,
                    code: nextCode,
                    label: "Nouvelle localisation racine",
                    typeCode: "L",
                    description: "",
                    children: [],
                  };
                  return [...prev, newNode];
                });
              }}
              onExpandAll={() => locationTreeRef.current?.expandAll?.()}
              onCollapseAll={() =>
                locationTreeRef.current?.collapseAll?.()
              }
              onResetFilter={() => setSearchTerm("")}
              onExport={() => {
                const blob = new Blob(
                  [JSON.stringify(locationTree, null, 2)],
                  { type: "application/json" }
                );
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "lbs-locations.json";
                a.click();
                URL.revokeObjectURL(url);
              }}
            />

            <div
              style={{
                flex: 1,
                overflow: "auto",
                paddingLeft: 0,
                paddingRight: 250,
              }}
            >
              <CoreliaTreeView
                ref={locationTreeRef}
                data={locationTree}
                onChange={setLocationTree}
                onNodeSelect={(n) => console.log("Select L:", n)}
                onNodeHover={() => {}}
                allowEdit={true}
                allowDragDrop={true}
                allowMultiroot={true}
                filter={searchTerm}
                highlight={null}
              />
            </div>
          </div>
        ),
      },
    ],
  };

  /* ============================================================
     ZONE D2 — Informations Location
  ============================================================ */
  const zoneD2 = {
    style: baseRightZoneStyle,
    gap: 12,
    sectionGap: 6,
    sections: [
      {
        icon: "/assets/icones/container/shield.svg",
        title: "Informations — LBS (Locations)",
        content: (
          <div
            style={{
              fontSize: theme.fontSizes.sm,
              color: theme.colors.text.body,
            }}
          >
            <p>
              Cette vue représente la décomposition géographique / par
              localisation (Location Breakdown Structure).
            </p>
            <p>
              Le type <code>L</code> applique la forme <code>triangle</code> et
              la couleur <code>{theme.systemBreakdown.getColor("L")}</code>.
            </p>
          </div>
        ),
      },
    ],
  };

  /* ============================================================
     ZONE E1 — Discipline (DBS) — CoreliaTreeView
  ============================================================ */
  const zoneE1 = {
    style: baseLeftZoneStyle,
    gap: 16,
    sectionGap: 6,
    sections: [
      {
        title: "Structure disciplinaire (DBS — Disciplines)",
        content: (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 10,
              width: "100%",
              height: "100%",
            }}
          >
            <TreeViewSideToolbar
              onAddRoot={() => {
                setDisciplineTree((prev) => {
                  const nextCode = getNextRootCode("D", prev);
                  const newNode = {
                    id: nextCode,
                    code: nextCode,
                    label: "Nouvelle discipline racine",
                    typeCode: "CIV", // valeur par défaut, discipline à ajuster ensuite
                    description: "",
                    children: [],
                  };
                  return [...prev, newNode];
                });
              }}
              onExpandAll={() => disciplineTreeRef.current?.expandAll?.()}
              onCollapseAll={() =>
                disciplineTreeRef.current?.collapseAll?.()
              }
              onResetFilter={() => setSearchTerm("")}
              onExport={() => {
                const blob = new Blob(
                  [JSON.stringify(disciplineTree, null, 2)],
                  { type: "application/json" }
                );
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "dbs-disciplines.json";
                a.click();
                URL.revokeObjectURL(url);
              }}
            />

            <div
              style={{
                flex: 1,
                overflow: "auto",
                paddingLeft: 0,
                paddingRight: 250,
              }}
            >
              <CoreliaTreeView
                ref={disciplineTreeRef}
                data={disciplineTree}
                onChange={setDisciplineTree}
                onNodeSelect={(n) => console.log("Select D:", n)}
                onNodeHover={() => {}}
                allowEdit={true}
                allowDragDrop={true}
                allowMultiroot={false}
                filter={searchTerm}
                highlight={null}
              />
            </div>
          </div>
        ),
      },
    ],
  };

  /* ============================================================
     ZONE E2 — Informations Discipline
  ============================================================ */
  const zoneE2 = {
    style: baseRightZoneStyle,
    gap: 12,
    sectionGap: 6,
    sections: [
      {
        icon: "/assets/icones/container/shield.svg",
        title: "Informations — DBS (Disciplines)",
        content: (
          <div
            style={{
              fontSize: theme.fontSizes.sm,
              color: theme.colors.text.body,
            }}
          >
            <p>
              Cette vue représente la structuration par disciplines
              d’ingénierie (CIV, ARC, ELE, ...).
            </p>
            <p>
              Les couleurs proviennent de <code>theme.SystemDisciplines</code>,
              la forme reste un cercle.
            </p>
          </div>
        ),
      },
    ],
  };

  /* ============================================================
     ZONE F1 — Cross-View Matrix (nouvelle version)
  ============================================================ */
  const zoneF1 = {
    style: baseLeftZoneStyle,
    gap: 16,
    sectionGap: 10,
    sections: [
      {
        title: "Cross-View Matrix",
        content: (
          <div
            style={{
              padding: 5,
              overflow: "auto",
              height: "100%",
              minHeight: 500,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CrossView
              pairs={CROSS_VIEW_PAIRS}
              trees={{
                A: activityTree,
                F: functionTree,
                P: productTree,
                L: locationTree,
                D: disciplineTree,
              }}
              relationsByPair={crossRelations}
              onRelationsByPairChange={setCrossRelations}
              height={650}
              
            />
          </div>
        ),
      },
    ],
  };

  /* ============================================================
     ZONE F2 — Informations Cross-View
  ============================================================ */
  const zoneF2 = {
    style: baseRightZoneStyle,
    gap: 12,
    sectionGap: 6,
    sections: [
      {
        icon: "/assets/icones/container/shield.svg",
        title: "Informations — Cross-View",
        content: (
          <div
            style={{
              fontSize: theme.fontSizes.sm,
              color: theme.colors.text.body,
            }}
          >
            <p>
              La matrice croisée permet de relier deux découpages (Activity /
              Function / Product / Location / Discipline).
            </p>
            <p>
              Chaque couple d’analyse dispose de sa propre matrice. Les
              relations sont stockées dans{" "}
              <code>crossRelations</code> sous la forme{" "}
              <code>{"{\"A→F\": {\"idX|idY\": true}, ...}"}</code>.
            </p>
          </div>
        ),
      },
    ],
  };

  /* ============================================================
     TABS — avec refresh des zones 1 via key={…}
  ============================================================ */
  const TabA = (
    <div style={tabStyle}>
      <ContainerBase key={`A1-${activeTab}`} {...zoneA1} />
      <ContainerBase {...zoneA2} />
    </div>
  );

  const TabB = (
    <div style={tabStyle}>
      <ContainerBase key={`B1-${activeTab}`} {...zoneB1} />
      <ContainerBase {...zoneB2} />
    </div>
  );

  const TabC = (
    <div style={tabStyle}>
      <ContainerBase key={`C1-${activeTab}`} {...zoneC1} />
      <ContainerBase {...zoneC2} />
    </div>
  );

  const TabD = (
    <div style={tabStyle}>
      <ContainerBase key={`D1-${activeTab}`} {...zoneD1} />
      <ContainerBase {...zoneD2} />
    </div>
  );

  const TabE = (
    <div style={tabStyle}>
      <ContainerBase key={`E1-${activeTab}`} {...zoneE1} />
      <ContainerBase {...zoneE2} />
    </div>
  );

  const TabF = (
    <div style={tabStyle}>
      <ContainerBase key={`F1-${activeTab}`} {...zoneF1} />
      <ContainerBase {...zoneF2} />
    </div>
  );

  const tabs = [
    { label: "Activity", content: TabA },
    { label: "Function", content: TabB },
    { label: "Product", content: TabC },
    { label: "Location", content: TabD },
    { label: "Discipline", content: TabE },
    { label: "Cross-View", content: TabF },
  ];

  /* ============================================================
     RENDER
  ============================================================ */
  return (
    <div
      style={{
        padding: 0,
        margin: 0,
        width: "100%",
        height: "100%",
        overflowX: "hidden",
        overflowY: "visible",
      }}
    >
      <ToolbarHeadSubpage
        style={{
          padding: 24,
          height: 50,
          minHeight: 50,
          overflow: "visible",
          background: "transparent",
        }}
        left={[
          <Button
            key="btn-new"
            variant="blue"
            onClick={() => alert("Action fictive du template")}
          >
            Nouvel élément
          </Button>,
        ]}
        center={[
          <DropdownMulti
            key="dd-cat"
            label="Catégorie"
            options={demoOptions}
            selectedValues={selectedCategories}
            onChange={setSelectedCategories}
            width={300}
            height={32}
          />,
          <TextFilter
            key="tf-search"
            label="Recherche"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Tapez un texte..."
            style={{ marginBottom: 0 }}
          />,
        ]}
        right={[
          <Switch
            key="sw-actifs"
            label="Actifs"
            enabled={switchOn}
            onChange={setSwitchOn}
            showText={false}
            size="small"
          />,
        ]}
      />

      <TabsBase tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
    </div>
  );
};

/* ============================================================
   STYLE COMMUN TABS
============================================================ */
const tabStyle = {
  display: "flex",
  flexDirection: "row",
  gap: 10,
  padding: 10,
  overflowY: "clip",
  overflowX: "hidden",
  minWidth: "fit-content",
  boxSizing: "border-box",
};

export default Subpage_102;
